import { useCallback, useEffect, useRef, useState } from 'react';
import { actions, type Action } from '../api/actions';
import { shrinkPhoto } from '../offline/shrink';
import {
  IconCamera,
  IconCheck,
  IconCircleAlert,
  IconClipboardList,
  IconPaperclip,
  IconRefreshCw,
  IconTrash2,
  IconTriangleAlert,
  IconUndo2,
} from '../icons';
import { formatDate, formatNumber } from '../i18n';
import { describeError, useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { Drawer } from '../ui/Drawer';
import { EmptyState } from '../ui/EmptyState';
import { Alert, Field, Textarea, TextInput } from '../ui/Field';
import { Card, PageHeader } from '../ui/Page';
import { Table, type Column } from '../ui/Table';

/**
 * The jobs a visit left behind, and what has become of them.
 *
 * <p>Two audiences on one screen, told apart by what the action is waiting for. "Mine" is
 * the store manager's list of things to do; the approval list is somebody else's job, and
 * the product will not let those be the same person on the same action.</p>
 */

type Tab = 'mine' | 'approval' | 'overdue' | 'all';

export function Actions() {
  const t = useTranslate();
  const [tab, setTab] = useState<Tab>('mine');
  const [rows, setRows] = useState<Action[]>([]);
  const [open, setOpen] = useState<Action | null>(null);
  const picker = useRef<HTMLInputElement>(null);
  const camera = useRef<HTMLInputElement>(null);

  /** The file on its way up, said on screen so a slow line does not read as a frozen panel. */
  const [uploading, setUploading] = useState<{ name: string; size: number } | null>(null);

  const [response, setResponse] = useState('');
  const [note, setNote] = useState('');
  const [plannedFor, setPlannedFor] = useState('');
  const [reason, setReason] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [panelError, setPanelError] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const all = await actions.list(
        tab === 'mine' ? { mine: true } : tab === 'overdue' ? { overdue: true } : {},
      );

      setRows(tab === 'approval' ? all.filter((a) => a.awaitsMyApproval) : all);
      setFailed(false);
      setError(null);
    } catch (e) {
      setFailed(true);
      setError(describeError(e, 'error.listFailed'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    void reload();
  }, [reload]);

  function show(row: Action) {
    setPanelError(null);
    setResponse('');
    setNote('');
    setReason('');
    setPlannedFor(new Date().toISOString().slice(0, 10));
    setOpen(row);
  }

  /**
   * A file attached as evidence: a photograph brought down to a size a store's mobile line
   * sends in a second or two (shrink.ts), then sent while the panel says what is going up.
   * Six megabytes from the phone took two seconds on the office line and would take ten to
   * twenty-five on a store's (test team, 4 November).
   */
  async function attach(action: Action, file: File) {
    if (busy || uploading) return;

    // Said from the first moment: shrinking a photo takes a second or two on a phone, and a
    // panel that does nothing for that long gets the button pressed again.
    setUploading({ name: file.name, size: file.size });
    try {
      const ready = await shrinkPhoto(file);
      setUploading({ name: ready.name, size: ready.size });
      await act(() => actions.attach(action.id, ready));
    } finally {
      setUploading(null);
    }
  }

  async function act(what: () => Promise<Action>) {
    if (busy) return;

    setBusy(true);
    try {
      const fresh = await what();
      setOpen(fresh);
      setPanelError(null);
      setResponse('');
      setNote('');
      setReason('');
      await reload();
    } catch (e) {
      setPanelError(describeError(e, 'error.actionFailed'));
    } finally {
      setBusy(false);
    }
  }

  const tone = (row: Action) =>
    row.status === 'Completed' ? 'comment' : row.isOverdue ? 'critical' : 'na';

  const columns: Column<Action>[] = [
    {
      id: 'what',
      header: t('actions.finding'),
      className: 'is-primary',
      cell: (row) => (
        <>
          {row.questionText}
          <div className="nv-hint">
            {row.reference ? <span className="nv-code">{row.reference}</span> : null} {row.problem}
          </div>
        </>
      ),
      skeletonWidth: '60%',
    },
    {
      id: 'where',
      header: t('actions.where'),
      cell: (row) => `${row.locationCode} · ${row.locationName}`,
      skeletonWidth: '30%',
    },
    {
      id: 'who',
      header: t('actions.assignee'),
      cell: (row) => row.assigneeName,
      skeletonWidth: '28%',
    },
    {
      id: 'due',
      header: t('actions.due'),
      cell: (row) => (
        <span className="nv-badge-group">
          {formatDate(row.dueOn)}
          {row.isOverdue ? (
            <Badge tone="critical" icon={IconTriangleAlert}>{t('actions.overdue')}</Badge>
          ) : null}
        </span>
      ),
      skeletonWidth: '34%',
    },
    {
      id: 'status',
      header: t('common.status'),
      cell: (row) => <Badge tone={tone(row)}>{t(`actions.status.${row.status}`)}</Badge>,
      skeletonWidth: '70px',
    },
  ];

  const tabs: { id: Tab; label: string }[] = [
    { id: 'mine', label: t('actions.mine') },
    { id: 'approval', label: t('actions.awaitingMe') },
    { id: 'overdue', label: t('actions.overdueTab') },
    { id: 'all', label: t('actions.all') },
  ];

  return (
    <>
      <PageHeader
        crumbs={[t('nav.actions')]}
        title={t('actions.title')}
        subtitle={t('actions.subtitle')}
      />

      {error ? <Alert>{error}</Alert> : null}

      <Card className="nv-table-card">
        <div className="nv-actions-bar">
          {tabs.map((x) => (
            <Button
              key={x.id}
              size="sm"
              variant={tab === x.id ? 'primary' : 'ghost'}
              aria-pressed={tab === x.id}
              onClick={() => setTab(x.id)}
            >
              {x.label}
            </Button>
          ))}
        </div>

        <Table
          caption={t('actions.title')}
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          status={loading ? 'loading' : failed ? 'error' : 'ready'}
          onRowClick={show}
          errorState={
            <EmptyState
              icon={IconCircleAlert}
              tone="danger"
              title={t('common.loadFailed')}
              text={t('common.loadFailedText')}
              action={<Button icon={IconRefreshCw} onClick={() => void reload()}>{t('common.retry')}</Button>}
            />
          }
          emptyState={
            <EmptyState
              icon={IconClipboardList}
              title={t('actions.empty')}
              text={t('actions.emptyText')}
            />
          }
        />
      </Card>

      <Drawer
        open={open !== null}
        title={open?.reference ?? t('actions.title')}
        onClose={() => setOpen(null)}
        footer={<Button onClick={() => setOpen(null)}>{t('common.close')}</Button>}
      >
        {open ? (
          <>
            <Field label={t('actions.finding')}>
              {() => <p>{open.questionText}</p>}
            </Field>

            <Field label={t('actions.problem')}>{() => <p>{open.problem}</p>}</Field>

            <div className="nv-drawer__section">
              <span className="nv-badge-group">
                <Badge tone={tone(open)}>{t(`actions.status.${open.status}`)}</Badge>
                <Badge tone="neutral">{t(`actions.kind.${open.kind}`)}</Badge>
                <Badge tone={open.isOverdue ? 'critical' : 'neutral'}>
                  {t('actions.dueOn', { date: formatDate(open.dueOn) })}
                </Badge>
              </span>
              <p className="nv-hint">
                {t('actions.raisedBy', { name: open.raisedBy, date: formatDate(open.raisedAt) })}
              </p>
            </div>

            {/* Every promise, oldest first. The point is that none of them was overwritten. */}
            {open.plans.length > 0 ? (
              <div className="nv-drawer__section">
                <h3 className="nv-h3">{t('actions.plans')}</h3>
                <ul className="nv-reasons">
                  {open.plans.map((p, i) => (
                    <li key={`${p.enteredAt}-${i}`}>
                      <strong>{formatDate(p.plannedFor)}</strong> — {p.reason}
                      <span className="nv-hint"> ({p.enteredBy})</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {open.steps.length > 0 ? (
              <div className="nv-drawer__section">
                <h3 className="nv-h3">{t('actions.chain')}</h3>
                <ul className="nv-reasons">
                  {open.steps.map((s) => (
                    <li key={s.order}>
                      <strong>{s.approver}</strong> — {t(`actions.decision.${s.decision}`)}
                      {s.note ? <div className="nv-hint">{s.note}</div> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            {/* Evidence sits above the answer, because it is what the answer will be read
                against. Still offered while the action is travelling the chain: an approver
                asking for a clearer photograph should not need the whole thing sent back. */}
            {open.evidence.length > 0 || open.status === 'Open' || open.status === 'SentBack'
              || open.status === 'InApproval' ? (
              <div className="nv-drawer__section">
                <h3 className="nv-h3">{t('actions.evidence')}</h3>
                <p className="nv-hint">{t('actions.evidenceHint')}</p>

                {open.evidence.length > 0 ? (
                  <ul className="nv-files">
                    {open.evidence.map((file) => (
                      <li key={file.id}>
                        <IconPaperclip size={16} />

                        <span className="nv-files__name">
                          <strong>{file.fileName ?? t('actions.evidence')}</strong>
                          <span className="nv-hint">
                            {t('actions.evidenceBy', {
                              name: file.uploadedBy,
                              date: formatDate(file.uploadedAt),
                            })}
                          </span>
                        </span>

                        {file.url ? (
                          <a
                            className="nv-btn nv-btn--ghost nv-btn--sm"
                            href={file.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('actions.evidenceOpen')}
                          </a>
                        ) : (
                          <span className="nv-hint">{t('actions.evidenceGone')}</span>
                        )}

                        {open.status !== 'Completed' && open.status !== 'Unresolved' ? (
                          <IconButton
                            label={t('common.delete')}
                            icon={IconTrash2}
                            tone="danger"
                            disabled={busy}
                            onClick={() => void act(() => actions.detach(open.id, file.id))}
                          />
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="nv-hint">{t('actions.evidenceNone')}</p>
                )}

                {open.status !== 'Completed' && open.status !== 'Unresolved' ? (
                  <>
                    <input
                      ref={picker}
                      type="file"
                      className="nv-sr-only"
                      aria-label={t('actions.attach')}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void attach(open, file);
                        e.target.value = '';
                      }}
                    />
                    {/* The phone's own camera, as the audit offers it. Kept wide on type for the
                        reason Audit.tsx gives; the photo is shrunk before it goes. */}
                    <input
                      ref={camera}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      className="nv-sr-only"
                      aria-label={t('actions.takePhoto')}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void attach(open, file);
                        e.target.value = '';
                      }}
                    />
                    <div className="nv-row">
                      <Button
                        size="sm"
                        icon={IconCamera}
                        disabled={busy || uploading !== null}
                        onClick={() => camera.current?.click()}
                      >
                        {t('actions.takePhoto')}
                      </Button>
                      <Button
                        size="sm"
                        icon={IconPaperclip}
                        disabled={busy || uploading !== null}
                        onClick={() => picker.current?.click()}
                      >
                        {t('actions.attach')}
                      </Button>
                    </div>
                    {uploading ? (
                      <p className="nv-hint" role="status">
                        {t('actions.uploading', {
                          name: uploading.name,
                          size: formatNumber(uploading.size / 1048576, { maximumFractionDigits: 1 }),
                        })}
                      </p>
                    ) : null}
                  </>
                ) : null}
              </div>
            ) : null}

            {open.response ? (
              <Field label={t('actions.response')}>{() => <p>{open.response}</p>}</Field>
            ) : null}

            {/* What the owner can do, while it is with them. */}
            {open.isMine && (open.status === 'Open' || open.status === 'SentBack') ? (
              <>
                {open.needsNewPlan ? (
                  <Alert tone="warning">{t('actions.needsNewPlan')}</Alert>
                ) : null}

                <div className="nv-drawer__section">
                  <h3 className="nv-h3">{t('actions.newPlan')}</h3>
                  <p className="nv-hint">{t('actions.newPlanHint')}</p>

                  <Field label={t('actions.plannedFor')}>
                    {({ id }) => (
                      <TextInput
                        id={id}
                        type="date"
                        value={plannedFor}
                        onChange={(e) => setPlannedFor(e.target.value)}
                      />
                    )}
                  </Field>

                  <Field label={t('actions.reason')}>
                    {({ id }) => (
                      <Textarea id={id} value={reason} onChange={(e) => setReason(e.target.value)} />
                    )}
                  </Field>

                  <Button
                    disabled={!plannedFor || !reason.trim() || busy}
                    onClick={() => void act(() => actions.plan(open.id, plannedFor, reason.trim()))}
                  >
                    {t('actions.addPlan')}
                  </Button>
                </div>

                <Field label={t('actions.response')} required>
                  {({ id }) => (
                    <Textarea
                      id={id}
                      placeholder={t('actions.responsePlaceholder')}
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                    />
                  )}
                </Field>

                <div className="nv-actions-bar">
                  <Button
                    variant="primary"
                    icon={IconCheck}
                    disabled={!response.trim() || busy || open.needsNewPlan}
                    onClick={() => void act(() => actions.respond(open.id, response.trim()))}
                  >
                    {t('actions.done')}
                  </Button>

                  <Button
                    disabled={!response.trim() || busy}
                    onClick={() => void act(() => actions.unresolved(open.id, response.trim()))}
                  >
                    {t('actions.cannot')}
                  </Button>
                </div>
              </>
            ) : null}

            {/* And what an approver can do, when it is their step. */}
            {open.awaitsMyApproval && open.status === 'InApproval' ? (
              <div className="nv-drawer__section">
                <h3 className="nv-h3">{t('actions.yourDecision')}</h3>

                <Field label={t('actions.note')}>
                  {({ id }) => (
                    <Textarea id={id} value={note} onChange={(e) => setNote(e.target.value)} />
                  )}
                </Field>

                <div className="nv-actions-bar">
                  <Button
                    variant="primary"
                    icon={IconCheck}
                    disabled={busy}
                    onClick={() => void act(() => actions.approve(open.id, note.trim() || undefined))}
                  >
                    {t('actions.approve')}
                  </Button>

                  <Button
                    icon={IconUndo2}
                    disabled={!note.trim() || busy}
                    onClick={() => void act(() => actions.sendBack(open.id, note.trim()))}
                  >
                    {t('actions.sendBack')}
                  </Button>
                </div>

                <p className="nv-hint">{t('actions.sendBackHint')}</p>
              </div>
            ) : null}

            {panelError ? <Alert>{panelError}</Alert> : null}
          </>
        ) : null}
      </Drawer>
    </>
  );
}
